import type { VirtualItem } from '@tanstack/virtual-core'
import type { CSSResult, PropertyValues, TemplateResult } from 'lit'
import * as checkedIcon from '@nordhealth/icons/lib/assets/interface-checked.js'
import * as closeIcon from '@nordhealth/icons/lib/assets/interface-close-small.js'
import * as dropdownIcon from '@nordhealth/icons/lib/assets/interface-dropdown-small.js'
import { VirtualizerController } from '@tanstack/lit-virtual'
import { html, isServer, LitElement, nothing, unsafeCSS } from 'lit'
import { property, query, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { repeat } from 'lit/directives/repeat.js'
import { styleMap } from 'lit/directives/style-map.js'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'

import { groupBy } from '../common/collection.js'
import { ActiveDescendantController } from '../common/controllers/ActiveDescendantController.js'
import { FormDataController } from '../common/controllers/FormDataController.js'
import { LightDismissController } from '../common/controllers/LightDismissController.js'
import { ResizeController } from '../common/controllers/ResizeController.js'
import { StateAttributesController } from '../common/controllers/StateAttributesController.js'
import { observe } from '../common/decorators/observe.js'
import { cond } from '../common/directives/cond.js'
import { NordEvent } from '../common/events.js'
import { AutocompleteMixin } from '../common/mixins/AutocompleteMixin.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import { FieldControlMixin } from '../common/mixins/FieldControlMixin.js'
import { FloatingMixin } from '../common/mixins/FloatingComponentMixin.js'
import { InputMixin } from '../common/mixins/InputMixin.js'
import { SizeMixin } from '../common/mixins/SizeMixin.js'
import Icon from '../icon/Icon.js'
import { LocalizeController } from '../localization/LocalizeController.js'
import '../popout/Popout.js'
import '../spinner/Spinner.js'

Icon.registerIcon(checkedIcon)
Icon.registerIcon(closeIcon)
Icon.registerIcon(dropdownIcon)

/** Per-instance counter so light-DOM ids (`aria-activedescendant`, etc.) stay unique. */
let comboboxCount = 1

/** Format a measured pixel size for a custom property (undefined → unset). */
const px = (value?: number) => (value ? `${value}px` : undefined)

/**
 * A single option in a combobox.
 */
export interface ComboboxOption {
  /** The value submitted with the form and tracked in `value`. */
  value: string
  /** The human-readable text shown in the list and the input. */
  label: string
  /** Optional group heading used to cluster related options together. */
  group?: string
  /** When `true`, the option cannot be selected. */
  disabled?: boolean
  /** Optional rich HTML rendered instead of the plain `label` text. */
  html?: string
  /** Arbitrary data attached to the option, passed back to `renderOption`. */
  [key: string]: unknown
}

/**
 * Re-prefixes an authored, tag-scoped stylesheet for another tag name so a
 * sibling light-DOM component (e.g. `nord-autocomplete`) can reuse the exact
 * same `.n-*` rules without duplicating them. `Combobox.css` is the single
 * source of truth; this swaps the top-level `nord-combobox` selector token.
 */
export function retagComboboxStyles(styles: CSSResult, tag: string): CSSResult {
  // The stylesheet is imported via the lit-css transform (a `CSSResult`); reading
  // `cssText` and re-wrapping the tag-swapped text keeps the rules authored once.
  return unsafeCSS(styles.cssText.replace(/nord-combobox/g, tag))
}

/**
 * `ComboboxBase` is the (unregistered) shared base for `nord-combobox` (pick an
 * option, or several) and `nord-autocomplete` (free-text input with suggestions
 * where the typed text is the value). It owns every piece of the dropdown that
 * the two share — options, filtering, virtualization, keyboard navigation, the
 * popout, light-dismiss, ARIA wiring and rendering — and exposes a set of
 * protected hooks for the handful of behaviours that differ between a select and
 * a free-text control.
 *
 * Base UI parity: Autocomplete is the Combobox primitive with
 * `selectionMode="none"` + `fillInputOnItemPress`.
 *
 * Subclasses MUST `injectLegacyLightDomStyles(this, <their stylesheet>)` in
 * `connectedCallback` (the base does not import a stylesheet, since the rules are
 * tag-scoped and each tag needs its own copy — see `retagComboboxStyles`).
 *
 * Not a registered custom element: it has no `@customElement` decorator and no
 * tag name, so the manifest config marks it `internal` and the React/Vue type
 * generators skip it.
 */
export default abstract class ComboboxBase extends DraftComponentMixin(
  SizeMixin(AutocompleteMixin(FieldControlMixin(InputMixin(FloatingMixin(LitElement))))),
) {
  /** Stable, unique id base for the light-DOM ARIA wiring of this instance. */
  private comboboxId = `${this.localName || 'nord-combobox'}-${comboboxCount++}`

  // The built-in `<label for>` (from FieldControlMixin) must point at the real
  // focusable input, so override the mixin's default id with the input's id.
  protected override inputId = `${this.comboboxId}-input`

  protected inputRef = createRef<HTMLInputElement>()

  // Both variants widen `value` beyond the controller's `InputMixinInterface`
  // host type. The controller only reads `form`/`name`/`disabled` and the
  // `value()` option (not `host.value`), so casting the host is safe.
  private formData = new FormDataController(this as ComboboxBase & { value: string }, {
    value: () => this.formValue,
  })

  /**
   * Remembers value→label pairs seen from any option so a preselected value
   * that has since left the loaded options still renders its label.
   */
  protected labelCache = new Map<string, string>()

  protected localize = new LocalizeController<'nord-combobox'>(this)

  /** Re-renders when the host's forwarded ARIA labelling attributes change. */
  private ariaObserver?: MutationObserver

  private widthObserver?: ResizeObserver

  @query('nord-popout', true) private popout?: HTMLElement & {
    show: () => Promise<unknown>
    hide: (moveFocusToButton?: boolean) => Promise<unknown>
  }

  private dismiss = new LightDismissController(this, {
    isOpen: () => this.open,
    onDismiss: () => this.hide(),
    isDismissible: node => node !== this,
    dismissOnOverlayOpen: true,
  })

  /**
   * Reflects the control's state to Base-UI-style `data-*` attributes
   * (`data-invalid`, `data-focused`) so consumers can style the whole control.
   * `trackFocus` keeps `data-focused` present while focus stays within the host
   * — e.g. moving between the input and the clear button.
   */
  private stateAttributes = new StateAttributesController(
    this,
    () => ({ invalid: this.invalid }),
    { trackFocus: true },
  )

  /**
   * The options to show in the combobox.
   */
  @property({ attribute: false }) options: ComboboxOption[] = []

  /**
   * Placeholder text to display within the input.
   */
  @property({ reflect: true }) placeholder?: string

  /**
   * Use external filtering mode. When set to `true`, the component will not
   * perform internal text-based filtering and expects the consumer to update
   * the `options` in response to the `input` event.
   */
  @property({ reflect: true, type: Boolean, attribute: 'external-filtering' }) externalFiltering = false

  /**
   * Shows a spinner in the control and listbox while asynchronous results are
   * loading. Pair with `external-filtering` for async search.
   */
  @property({ reflect: true, type: Boolean }) loading = false

  /**
   * Highlights the first option after filtering so pressing Enter selects it.
   */
  @property({ reflect: true, type: Boolean, attribute: 'auto-highlight' }) autoHighlight = false

  /**
   * Marks the control as invalid, applying error styling and setting
   * `aria-invalid` on the input.
   */
  @property({ reflect: true, type: Boolean }) invalid = false

  // The control's own invalid state for the field context: its `invalid` prop or
  // a built-in `error` (when used standalone).
  protected override get ownInvalid() {
    return this.invalid || super.ownInvalid
  }

  /**
   * Marks the control read-only: the value stays visible, focusable and
   * submitted with the form, but the listbox can't be opened and the value can't
   * be changed (no typing, navigation, selection or clearing). Unlike `disabled`,
   * a read-only control still participates in form submission.
   */
  @property({ reflect: true, type: Boolean }) readonly = false

  /**
   * Whether clicking the input opens the listbox. Defaults to `true`. Set to
   * `false` to only open on typing or a trigger (chevron) click. Focusing the
   * input (e.g. tabbing in) never opens the list on its own. Mirrors Base UI's
   * `openOnInputClick`.
   */
  @property({ reflect: true, type: Boolean, attribute: 'open-on-input-click' }) openOnInputClick = true

  /**
   * Message shown when no options match the query. Defaults to "No items found.".
   */
  @property({ reflect: true, attribute: 'no-results-text' }) noResultsText?: string

  /**
   * Virtualizes (windows) the option list so only the rows in view are rendered,
   * keeping very large lists (thousands of options) fast. Rows are assumed to be a
   * uniform height, which is measured from the first option. Virtualization is
   * skipped for grouped lists, whose labels and separators break that assumption —
   * grouped lists are expected to be short and curated.
   */
  @property({ reflect: true, type: Boolean }) virtualized = false

  /**
   * Custom renderer for the content of each option. Receives the option and
   * should return a Lit `TemplateResult` or a string. When omitted, the
   * option's `html` (if present) or `label` is rendered.
   */
  @property({ attribute: false }) renderOption?: (option: ComboboxOption) => TemplateResult | string

  /** The current search query in the combobox input. */
  @state() protected search = ''

  /**
   * Whether the user has actually typed into the input since it was last
   * opened/committed. Until they do, a single-select shows the committed
   * selection's label in the input (editable) rather than an empty filter —
   * matching Base UI, where reopening keeps the selected label visible and the
   * list unfiltered until the first keystroke. Set on typing; cleared on commit
   * and on close.
   */
  @state() protected queryDirty = false

  /**
   * Tracks the active (highlighted) row of the virtual-focus listbox: the focus
   * stays on the input while this roams the rows and surfaces as
   * `aria-activedescendant`. Owns the wrap / skip-disabled / move math the
   * combobox previously duplicated. The out-of-range create row (index ===
   * `filteredOptions.length`) reports as enabled (`filteredOptions[i]` is
   * `undefined`, so `!undefined?.disabled` is `true`) and maps to its create-row
   * id, matching the create-row semantics.
   */
  protected activeDescendant = new ActiveDescendantController(this, {
    count: () => this.rowCount,
    isEnabled: i => !this.filteredOptions[i]?.disabled,
    optionId: i =>
      this.createIndex !== -1 && i === this.createIndex ? this.createId : this.optionId(i),
  })

  /** The scroll container for the options; the virtualizer's scroll element. */
  @query('.n-listbox') private listboxEl?: HTMLElement

  /**
   * Containers for the `start`/`end` slot content. The control renders into the
   * light DOM, so real `<slot>` elements don't project; instead these act as
   * landing zones and the matching host children (`slot="start"`/`slot="end"`)
   * are relocated into them. A `ResizeController` per zone feeds the measured
   * inline size into a custom property so the input padding can clear the slot
   * (mirrors `nord-input`).
   */
  @query('.n-slot-start') private startSlot?: HTMLElement
  @query('.n-slot-end') private endSlot?: HTMLElement
  private startObserver = new ResizeController(this, () => this.startSlot as HTMLElement)
  private endObserver = new ResizeController(this, () => this.endSlot as HTMLElement)

  /** Re-renders when the host's `slot="start"`/`slot="end"` children change. */
  private slotObserver?: MutationObserver

  /** Set when a keyboard move should scroll the active option into view. */
  protected scrollActivePending = false

  /** Row-height estimate used until the uniform row height is measured once. */
  private static readonly VIRTUAL_ROW_ESTIMATE = 36

  /** Extra rows rendered above and below the viewport to smooth scrolling. */
  private static readonly VIRTUAL_OVERSCAN = 6

  /**
   * The measured uniform row height, sampled once from the first rendered row.
   * Option rows are a fixed height, so a single sample feeds `estimateSize` for
   * the whole list — far cheaper and more stable than re-measuring every row on
   * every render, which fed a `measure → onChange → re-render → measure` loop
   * that could peg the main thread.
   */
  private measuredRowSize?: number

  /**
   * Windows the flat option list (see `virtualized`) with TanStack Virtual: only
   * the rows in view are rendered, sized from the measured uniform row height,
   * and the active option is kept on-screen via `scrollToIndex`. Its `onChange`
   * re-renders the host on scroll/resize; `count` is kept in sync in `willUpdate`.
   */
  private virtualizer = new VirtualizerController<HTMLElement, HTMLElement>(this, {
    count: 0,
    overscan: ComboboxBase.VIRTUAL_OVERSCAN,
    estimateSize: () => this.measuredRowSize ?? ComboboxBase.VIRTUAL_ROW_ESTIMATE,
    getScrollElement: () => this.listboxEl ?? null,
    getItemKey: index => this.filteredOptions[index]?.value ?? index,
  })

  /**
   * Whether the user has moved the active option with the keyboard since the
   * last filter change. Used to decide if a bare Enter (no navigation) selects
   * the highlighted option — only when `autoHighlight` is set, the first option
   * becomes implicitly selectable.
   */
  protected navigated = false

  // ---------------------------------------------------------------------------
  // Variant hooks — overridable behaviour that distinguishes the select combobox
  // from the free-text autocomplete. Sensible defaults make the base behave as a
  // single-select combobox out of the box.
  // ---------------------------------------------------------------------------

  /**
   * Whether the input currently shows the transient query rather than the
   * committed selection's label. A single-select shows the query only once the
   * user starts typing (`queryDirty`); until then — including right after
   * reopening — the input keeps the selected option's label visible and
   * editable, matching Base UI. `multiple` shows the query always (the
   * selections live in chips, not the input).
   */
  protected get showsQueryInInput(): boolean {
    return this.queryDirty
  }

  /** The text shown in the input. Select: query while open, label when closed. */
  protected get displayValue(): string {
    // While the query is shown the input is a filter; otherwise it shows the
    // selected option's label.
    if (this.showsQueryInInput) {
      return this.search
    }

    if (!this.value || Array.isArray(this.value)) {
      return ''
    }

    return this.labelFor(this.value)
  }

  /** The placeholder forwarded to the inner input. */
  protected get inputPlaceholder(): string | undefined {
    return this.placeholder
  }

  /** The transient search text the list is filtered against. */
  protected get filterQuery(): string {
    return this.search
  }

  /** Whether an option is selected (drives `aria-selected` and the check icon). */
  protected isSelected(option: ComboboxOption): boolean {
    return this.values.includes(option.value)
  }

  /** Serialises `value` for the form. */
  protected get formValue(): string | undefined {
    const { value } = this

    if (Array.isArray(value)) {
      return value.length ? value.join(',') : undefined
    }

    return value || undefined
  }

  /** Whether there is any committed value (drives the clear affordance). */
  protected get hasValue(): boolean {
    return this.values.length > 0
  }

  /** The index of the create row, or `-1` when it isn't shown. Select-only. */
  protected get createIndex(): number {
    return -1
  }

  /** Whether the "Create" affordance should be offered for the current query. */
  protected get canCreate(): boolean {
    return false
  }

  /** Leading content inside the control (e.g. chips). None by default. */
  protected renderLeadingControl(): unknown {
    return nothing
  }

  /** The `aria-autocomplete` value for the inner input. */
  protected get ariaAutocomplete(): string {
    return 'list'
  }

  /** Commit the active option. Select: set value, close, restore focus. */
  protected selectOption(option: ComboboxOption): void {
    if (option.disabled) {
      return
    }

    // Remember the label so it survives the option leaving the result set.
    this.labelCache.set(option.value, option.label)

    this.value = option.value
    this.search = ''
    this.queryDirty = false
    this.commit()
    this.hide()
    // Restore focus to the input. Focus alone never opens the listbox, so no
    // guard is needed here.
    this.focus()
  }

  /**
   * Hook for the Enter key when the listbox is open and the create/option
   * branches have not handled it. Default: no-op (the base handles option
   * selection in `handleKeydown`). Subclasses (autocomplete) return `true` to
   * signal they fully handled Enter.
   */
  protected handleEnter(_e: KeyboardEvent): boolean {
    return false
  }

  /** Hook invoked when a key is pressed (before the switch). Autocomplete uses it. */
  protected onKeydown(_e: KeyboardEvent): void {}

  /** Hook invoked on input blur. Autocomplete reverts inline ghosts here. */
  protected onBlur(): void {}

  /** Hook invoked after each render (for inline-ghost selection, etc.). */
  protected afterUpdate(): void {}

  /** Hook invoked when the pointer hovers an option (autocomplete clears ghost state). */
  protected onOptionHover(): void {}

  /** Hook invoked when ArrowUp/ArrowDown moves the active option. */
  protected onMove(): void {}

  // ---------------------------------------------------------------------------

  /** Normalises `value` to an array regardless of single/multiple shape. */
  protected get values(): string[] {
    const { value } = this

    if (Array.isArray(value)) {
      return value
    }

    return value ? [value] : []
  }

  /** Upper bound on the label cache so long async sessions don't grow it forever. */
  private static labelCacheLimit = 500

  /** Records value→label pairs so labels survive options leaving the result set. */
  protected cacheLabels(options: ComboboxOption[]) {
    for (const option of options) {
      if (option.value) {
        this.labelCache.set(option.value, option.label)
      }
    }

    // Evict the oldest non-selected labels once over the limit (Maps iterate in
    // insertion order); never drop a label that's currently selected.
    if (this.labelCache.size <= ComboboxBase.labelCacheLimit) {
      return
    }

    const selected = new Set(this.values)
    for (const key of this.labelCache.keys()) {
      if (this.labelCache.size <= ComboboxBase.labelCacheLimit) {
        break
      }
      if (!selected.has(key)) {
        this.labelCache.delete(key)
      }
    }
  }

  /**
   * Memoised filter result. `filteredOptions` is read many times per render (and
   * per keyboard/scroll event via rowCount/activeOption/isVirtual), so the O(n)
   * filter + groupBy must not re-run on every access — that would defeat the
   * point of virtualizing a large list. Recomputed only when the options, the
   * query, or the filtering mode change; the group map is cached alongside.
   */
  private filteredCache?: {
    options: ComboboxOption[]
    query: string
    external: boolean
    result: ComboboxOption[]
    hasGroups: boolean
    groups?: Map<string | undefined, ComboboxOption[]>
  }

  /**
   * The options the control filters and renders from. The base uses the
   * `options` property directly; `Combobox` overrides this to merge declarative
   * `<nord-combobox-option>` children and auto-created options.
   */
  protected get resolvedOptions(): ComboboxOption[] {
    return this.options
  }

  protected get filtered() {
    const options = this.resolvedOptions
    const query = this.filterQuery
    const external = this.externalFiltering
    const cache = this.filteredCache
    if (cache && cache.options === options && cache.query === query && cache.external === external) {
      return cache
    }

    // Labels only change with the options, so re-cache only when those change.
    if (!cache || cache.options !== options) {
      this.cacheLabels(options)
    }

    const lower = query.toLocaleLowerCase()
    const matched = external || !query
      ? options
      : options.filter(option => option.label.toLocaleLowerCase().includes(lower))

    // Order by group so the flat list used for keyboard navigation (activeIndex,
    // Home/End, aria-activedescendant) matches the grouped display order — else
    // interleaved groups desync the two.
    const hasGroups = matched.some(option => option.group)
    const result = hasGroups
      ? Array.from(groupBy(matched, 'group'), ([, groupOptions]) => groupOptions).flat()
      : matched

    this.filteredCache = {
      options,
      query,
      external,
      result,
      hasGroups,
      groups: hasGroups ? groupBy(result, 'group') : undefined,
    }
    return this.filteredCache
  }

  /** The options remaining after filtering by the current query. */
  protected get filteredOptions(): ComboboxOption[] {
    return this.filtered.result
  }

  /** Total number of keyboard-navigable rows, including the create row. */
  protected get rowCount(): number {
    return this.filteredOptions.length + (this.canCreate ? 1 : 0)
  }

  /** The currently active option, used for `aria-activedescendant` and Enter. */
  protected get activeOption(): ComboboxOption | undefined {
    return this.filteredOptions[this.activeDescendant.activeIndex]
  }

  /**
   * Whether the option list is currently windowed. Only flat (non-grouped) lists
   * with at least one option are virtualized.
   */
  protected get isVirtual(): boolean {
    if (!this.virtualized) {
      return false
    }
    const { result, hasGroups } = this.filtered
    return result.length > 0 && !hasGroups
  }

  connectedCallback() {
    super.connectedCallback()

    // An external label (e.g. nord-field-label) names the control by setting
    // `aria-labelledby`/`aria-label` on the host; forward it to the inner input.
    this.ariaObserver ??= new MutationObserver(() => this.requestUpdate())
    this.ariaObserver.observe(this, {
      attributes: true,
      attributeFilter: ['aria-label', 'aria-labelledby'],
    })

    // Mirror the control width into a custom property so the dropdown matches it
    // (like the provet combobox), tracking responsive/container resizes.
    this.widthObserver ??= new ResizeObserver(() => this.syncAnchorInlineSize())
    this.widthObserver.observe(this)
    this.syncAnchorInlineSize()

    // Watch for `slot="start"`/`slot="end"` children being added or removed so
    // they get relocated into their landing zones on the next render.
    this.slotObserver ??= new MutationObserver(() => this.requestUpdate())
    this.slotObserver.observe(this, { childList: true })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.ariaObserver?.disconnect()
    this.ariaObserver = undefined
    this.widthObserver?.disconnect()
    this.widthObserver = undefined
    this.slotObserver?.disconnect()
    this.slotObserver = undefined
  }

  /**
   * Relocates the host's `slot="start"`/`slot="end"` children into the matching
   * landing zone inside the control. Only direct host children are considered,
   * and a child already sitting in its zone is left untouched, so repeated
   * updates are idempotent and don't disturb focus or pointer state.
   */
  private relocateSlots() {
    if (isServer) {
      return
    }

    const move = (name: 'start' | 'end', target?: HTMLElement) => {
      if (!target) {
        return
      }
      for (const child of Array.from(this.children)) {
        if (child.getAttribute('slot') === name && child.parentElement !== target) {
          target.appendChild(child)
        }
      }
    }

    move('start', this.startSlot)
    move('end', this.endSlot)
  }

  /** Mirrors the host (control) width into `--_n-combobox-anchor-inline-size`. */
  protected syncAnchorInlineSize() {
    const width = this.offsetWidth
    if (width) {
      this.style.setProperty('--_n-combobox-anchor-inline-size', `${width}px`)
    }
  }

  // Render in the light DOM so the control participates in the document tree
  // (form labelling, Tailwind), like the other new Nord components. The UI renders
  // into a layout-transparent (`display: contents`) child wrapper rather than the
  // host itself, so declarative `<nord-combobox-option>` children can live as
  // untouched siblings instead of being wiped by this element's own render.
  protected createRenderRoot() {
    if (isServer) {
      return this
    }
    const root = document.createElement('div')
    root.className = 'n-render-root'
    this.appendChild(root)
    return root
  }

  /**
   * Show the listbox programmatically.
   */
  show() {
    if (this.open || this.disabled || this.readonly) {
      return
    }

    const notCancelled = this.dispatchEvent(new NordEvent('open', { cancelable: true }))

    if (notCancelled) {
      this.open = true
    }
  }

  /**
   * Hide the listbox programmatically.
   */
  hide() {
    if (!this.open) {
      return
    }

    this.open = false

    this.onHide()

    this.dispatchEvent(new NordEvent('close'))
  }

  /**
   * Hook for what happens to the transient query when the listbox closes.
   * Select mode discards an abandoned filter query so the next open shows the
   * full list and the selected option's label, not the stale search.
   */
  protected onHide() {
    this.search = ''
    // Reopening starts clean: the input shows the committed label again and the
    // list is unfiltered until the user types.
    this.queryDirty = false
  }

  /**
   * Focus the control's input.
   */
  focus(options?: FocusOptions) {
    this.inputRef.value?.focus(options)
  }

  @observe('open', 'updated')
  protected handleOpenChange() {
    if (this.open) {
      void this.popout?.show()
      this.resetActiveIndex()
    }
    else {
      void this.popout?.hide(false)
    }
  }

  /**
   * Disabling the control while the listbox is open must close it. `show()`
   * guards against opening when disabled, but nothing closes an already-open
   * popout when `disabled` flips to `true` at runtime.
   */
  @observe('disabled')
  protected handleDisabledChange() {
    if (this.disabled && this.open) {
      this.hide()
    }
  }

  /** Row height changes with the size variant, so re-sample it on the next render. */
  @observe('size')
  protected resetRowSample() {
    this.measuredRowSize = undefined
  }

  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties)

    // Move any `slot="start"`/`slot="end"` host children into their landing
    // zones inside the control. The control is light DOM, so real `<slot>`
    // projection isn't available; relocating keeps the public API identical to
    // `nord-input` (`<nord-icon slot="start">`).
    this.relocateSlots()

    // Variant hook (e.g. autocomplete inline-ghost selection) runs first so it
    // can reach into the freshly-rendered input.
    this.afterUpdate()

    // Sample the uniform row height once so the virtualizer windows from the real
    // height instead of the estimate (no-op once sampled).
    this.sampleRowSize()

    // Scroll the active option into view after a keyboard move (never on hover).
    if (this.scrollActivePending) {
      this.scrollActivePending = false
      this.scrollActiveIntoView()
    }
  }

  /** Keeps `count` in sync with the filtered list before each render. */
  protected willUpdate(changed: PropertyValues) {
    super.willUpdate(changed)
    if (this.isVirtual) {
      const v = this.virtualizer.getVirtualizer()
      const count = this.filteredOptions.length
      if (v.options.count !== count) {
        v.setOptions({ ...v.options, count })
      }
    }
  }

  /**
   * Keeps the active row visible after a keyboard move. Virtualized option rows
   * ask the virtualizer to scroll the index into view (the row may not be in the
   * DOM yet); the create row and plain lists defer to the browser since their
   * element is always rendered.
   */
  private scrollActiveIntoView() {
    const listbox = this.listboxEl
    if (!listbox) {
      return
    }

    const activeIndex = this.activeDescendant.activeIndex
    if (this.isVirtual && activeIndex >= 0 && activeIndex !== this.createIndex) {
      this.virtualizer.getVirtualizer().scrollToIndex(activeIndex, { align: 'auto' })
      return
    }

    listbox.querySelector('.n-option.is-active')?.scrollIntoView({ block: 'nearest' })
  }

  /** Keeps `open` in sync if the popout closes itself (e.g. light dismiss). */
  private handlePopoutClose(e: Event) {
    // The popout dispatches its own bubbling+composed `close`; stop it at the
    // boundary so the combobox emits exactly one canonical `close` (from `hide`).
    e.stopPropagation()

    if (this.open) {
      this.hide()
    }
  }

  /** Swallows the popout's own `open` so the combobox emits a single `open`. */
  private handlePopoutOpen(e: Event) {
    e.stopPropagation()
  }

  /** Re-clamps the active index so it stays within the navigable rows. */
  @observe('search')
  protected resetActiveIndex() {
    // Start with no active row (-1) so nothing looks selected until the user
    // navigates — matching Base UI, where the first ArrowDown lands on the first
    // row. Two opt-ins pre-highlight a row instead:
    //   • `autoHighlight` highlights the first navigable row so a bare Enter commits it.
    //   • a `creatable` create row, when it is the only actionable row (no enabled
    //     option to choose), so a bare Enter creates the typed value (⏎ Kbd hint).
    if (this.autoHighlight) {
      this.activeDescendant.setActiveIndex(this.activeDescendant.nextEnabledIndex(0, 1))
    }
    else if (this.createIndex !== -1 && this.filteredOptions.every(option => option.disabled)) {
      this.activeDescendant.setActiveIndex(this.createIndex)
    }
    else {
      this.activeDescendant.reset()
    }
    this.navigated = false
    // Re-scroll to the (reset) active option so a fresh filter or reopen starts
    // at the top rather than wherever the list was previously scrolled.
    this.scrollActivePending = true
  }

  protected handleInput(e: Event) {
    e.stopPropagation()

    if (this.readonly) {
      return
    }

    const input = e.target as HTMLInputElement
    this.search = input.value
    // The user is now editing the filter, so the input shows the live query
    // rather than the committed label, and the list filters by it.
    this.queryDirty = true

    if (!this.open) {
      this.show()
    }

    /** Fired as the user types into the search input. */
    this.dispatchEvent(new NordEvent('input'))

    // Variant hook so autocomplete can commit the typed text as the live value.
    this.onInput(input)
  }

  /** Hook after the search query updates from typing. Autocomplete commits live. */
  protected onInput(_input: HTMLInputElement): void {}

  private handleBlur() {
    this.onBlur()
  }

  protected handleKeydown(e: KeyboardEvent) {
    // Read-only locks the value: no opening, navigating, selecting or clearing.
    if (this.readonly) {
      return
    }

    this.onKeydown(e)

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (!this.open) {
          this.show()
          return
        }
        this.move(1)
        break
      case 'ArrowUp':
        e.preventDefault()
        if (!this.open) {
          this.show()
          return
        }
        this.move(-1)
        break
      case 'End': {
        if (this.open) {
          e.preventDefault()
          if (this.activeDescendant.last()) {
            this.navigated = true
            this.scrollActivePending = true
          }
        }
        break
      }
      case 'Home': {
        if (this.open) {
          e.preventDefault()
          if (this.activeDescendant.first()) {
            this.navigated = true
            this.scrollActivePending = true
          }
        }
        break
      }
      case 'Enter':
        if (!this.open) {
          break
        }
        // Let a variant fully own Enter (autocomplete commits free text / fills).
        if (this.handleEnter(e)) {
          break
        }
        // The create row is an explicit affordance hinted with a ⏎ Kbd, so a
        // bare Enter always commits it when it is the active row.
        if (this.createIndex !== -1 && this.activeDescendant.activeIndex === this.createIndex) {
          e.preventDefault()
          this.emitCreate()
          break
        }
        // For options, a bare Enter (no navigation) only commits the highlighted
        // first row once it is implicitly selectable (navigated or autoHighlight).
        if ((this.navigated || this.autoHighlight) && this.activeOption) {
          e.preventDefault()
          this.selectOption(this.activeOption)
        }
        break
      case 'Escape':
        if (this.open) {
          e.preventDefault()
          this.hide()
        }
        break
      default:
        this.onUnhandledKeydown(e)
        break
    }
  }

  /** Hook for keys the base switch does not handle (Backspace chips, etc.). */
  protected onUnhandledKeydown(_e: KeyboardEvent): void {}

  /**
   * Fires the create affordance. Default is a no-op; the select combobox
   * overrides this to dispatch its `create` event.
   */
  protected emitCreate(): void {}

  private move(delta: number) {
    if (this.rowCount === 0) {
      return
    }

    this.onMove()

    // The controller does the index math: from "no active row" (-1, autocomplete
    // before the first navigation) ArrowDown lands on the first row and ArrowUp
    // on the last; otherwise it steps from the current row, skipping disabled
    // rows and wrapping. It returns `false` when every row is disabled (and there
    // is no create row), so we keep the listbox as-is rather than landing on an
    // unselectable row or marking it as navigated.
    if (!this.activeDescendant.move(delta)) {
      return
    }

    this.navigated = true
    this.scrollActivePending = true
  }

  protected commit() {
    this.requestUpdate()

    /**
     * Fired whenever the committed value is changed via user interaction.
     */
    this.dispatchEvent(new NordEvent('change'))
  }

  /** Resolves a value to its best-known label (current options, then cache). */
  protected labelFor(value: string): string {
    const option = this.resolvedOptions.find(o => o.value === value)
    return option?.label ?? this.labelCache.get(value) ?? value
  }

  render() {
    // `filteredOptions` caches value→label pairs as a side effect.
    const { result: options, hasGroups, groups } = this.filtered
    // Virtualize only flat, non-empty lists. `willUpdate` has already synced the
    // virtualizer's count, so the rendered window is current here.
    const isVirtual = this.virtualized && !hasGroups && options.length > 0
    const virtualItems = isVirtual ? this.virtualizer.getVirtualizer().getVirtualItems() : undefined
    // O(1) flat-index lookup for the grouped path (avoids indexOf per option).
    const indexByValue = hasGroups ? new Map(options.map((option, i) => [option.value, i])) : undefined
    const activeIndex = this.activeDescendant.activeIndex
    const activeOption = this.activeOption
    // Suppress aria-activedescendant when the active option is windowed out of a
    // virtualized list (e.g. after mouse-scrolling away from it), so it never
    // references a row that isn't in the DOM. The create row is always rendered.
    const activeRendered
      = !virtualItems
        || activeIndex === this.createIndex
        || virtualItems.some(item => item.index === activeIndex)
    const activeId
      = this.createIndex !== -1 && activeIndex === this.createIndex
        ? `${this.comboboxId}-create`
        : activeOption && activeRendered
          ? this.optionId(activeIndex)
          : undefined
    const listId = `${this.comboboxId}-list`
    const anchorId = `${this.comboboxId}-anchor`
    const showClear = this.showClear

    // Naming/description resolved by the FieldControlMixin getters (field context
    // in a field, built-in label/hint/error standalone, or host aria-* fallback).
    const ariaLabel = this.controlAriaLabel
    const ariaLabelledby = this.controlAriaLabelledby
    const ariaDescribedby = this.controlAriaDescribedby

    const input = html`
      <input
        ${ref(this.inputRef)}
        class="n-input"
        type="text"
        id="${this.comboboxId}-input"
        role="combobox"
        .value=${this.displayValue}
        placeholder=${ifDefined(this.inputPlaceholder)}
        ?disabled=${this.effectiveDisabled}
        ?readonly=${this.readonly}
        ?required=${this.required}
        autocomplete="off"
        autocapitalize="off"
        spellcheck="false"
        aria-label=${ifDefined(ariaLabel)}
        aria-labelledby=${ifDefined(ariaLabelledby)}
        aria-describedby=${ifDefined(ariaDescribedby)}
        aria-autocomplete=${this.ariaAutocomplete}
        aria-haspopup="listbox"
        aria-controls=${listId}
        aria-expanded=${this.open ? 'true' : 'false'}
        aria-activedescendant=${ifDefined(this.open ? activeId : undefined)}
        aria-invalid=${ifDefined(this.controlAriaInvalid)}
        @input=${this.handleInput}
        @blur=${this.handleBlur}
        @keydown=${this.handleKeydown}
      />
    `

    const leading = this.renderLeadingControl()

    return html`
      ${this.renderLabel()}
      <div class="n-combobox" id=${anchorId}>
        <div
          class=${classMap({
            'n-control': true,
            'is-invalid': this.effectiveInvalid,
          })}
          style=${styleMap({
            '--_n-combobox-start-inline-size': px(this.startObserver.inlineSize),
            '--_n-combobox-end-inline-size': px(this.endObserver.inlineSize),
          })}
          @click=${this.handleControlClick}
        >
          <span class="n-slot n-slot-start"></span>

          ${leading === nothing
            ? input
            : html`<div class="n-tags">${leading}${input}</div>`}

          <span class="n-slot n-slot-end"></span>

          ${this.loading
            ? html`<nord-spinner class="n-loading" size="s"></nord-spinner>`
            : nothing}

          ${showClear
            ? html`
                <button
                  class="n-clear"
                  type="button"
                  aria-label=${this.localize.term('clearLabel')}
                  ?disabled=${this.disabled || this.readonly}
                  @click=${this.handleClearClick}
                >
                  <nord-icon name="interface-close-small"></nord-icon>
                </button>
                ${this.showToggle
                  ? html`<span class="n-divider" aria-hidden="true"></span>`
                  : nothing}
              `
            : nothing}

          ${this.showToggle
            ? html`
                <button
                  class="n-toggle"
                  type="button"
                  tabindex="-1"
                  aria-hidden="true"
                  ?disabled=${this.disabled || this.readonly}
                  @click=${this.handleToggleClick}
                >
                  <nord-icon name="interface-dropdown-small"></nord-icon>
                </button>
              `
            : nothing}
        </div>

        <nord-popout
          id="${this.comboboxId}-popout"
          anchor=${anchorId}
          align=${ifDefined(this.align)}
          position=${ifDefined(this.position)}
          always-floating
          @open=${this.handlePopoutOpen}
          @close=${this.handlePopoutClose}
        >
          <div
            id=${listId}
            class="n-listbox"
            role=${ifDefined(this.rowCount ? 'listbox' : undefined)}
            aria-label=${ifDefined(this.rowCount ? ariaLabel : undefined)}
            aria-labelledby=${ifDefined(this.rowCount ? ariaLabelledby : undefined)}
            aria-multiselectable=${ifDefined(this.rowCount ? this.ariaMultiselectable : undefined)}
          >
            ${options.length === 0 && !this.canCreate
              ? this.renderEmpty()
              : hasGroups && groups && indexByValue
                ? Array.from(groups, ([group, groupOptions]) => this.renderGroup(group, groupOptions, indexByValue))
                : isVirtual && virtualItems
                  ? this.renderVirtualOptions(options, virtualItems)
                  : repeat(
                      options,
                      option => option.value,
                      (option, i) => this.renderListOption(option, i),
                    )}
            ${this.canCreate ? this.renderCreate() : nothing}
          </div>
        </nord-popout>
      </div>
      ${this.renderError()}
      ${this.isHintBelow ? this.renderHint() : nothing}
    `
  }

  /** Whether the clear (✕) affordance shows. Off by default (no `clearable`). */
  protected get showClear(): boolean {
    return false
  }

  /**
   * Whether the dropdown chevron toggle (and the divider before it) renders. On
   * by default — the combobox is a picker that opens its full list on click —
   * but hidden while `readonly`, where the value is shown but not interactive.
   * Autocomplete overrides this to `false`: like Base UI's autocomplete (which
   * renders no `Trigger`), it is a search input whose list appears on typing.
   */
  protected get showToggle(): boolean {
    return !this.readonly
  }

  /**
   * Drives the listbox `aria-multiselectable`. Single-select by default;
   * autocomplete overrides this to `undefined` so the attribute is omitted (it
   * has no selection model).
   */
  protected get ariaMultiselectable(): 'true' | 'false' | undefined {
    return 'false'
  }

  protected renderGroup(
    group: string | undefined,
    groupOptions: ComboboxOption[],
    indexByValue: Map<string, number>,
  ) {
    const groupId = `${this.comboboxId}-group-${group}`

    return html`
      <div role="group" aria-labelledby=${cond(group, groupId)}>
        ${group
          ? html`<div class="n-group-label" role="presentation" id=${groupId}>${group}</div>`
          : nothing}
        ${repeat(
          groupOptions,
          option => option.value,
          option => this.renderListOption(option, indexByValue.get(option.value) ?? 0),
        )}
      </div>
    `
  }

  /** Resolves the content shown for a single option. */
  protected optionContent(option: ComboboxOption): TemplateResult | unknown {
    if (this.renderOption) {
      return this.renderOption(option)
    }

    if (option.html) {
      return unsafeHTML(option.html)
    }

    return option.label
  }

  /**
   * Samples the uniform row height once from the first rendered virtual row, then
   * re-measures the virtualizer so the window is sized from the real height rather
   * than the estimate. Runs only while the sample is missing and a laid-out row is
   * present, so it never becomes a per-render measurement loop.
   */
  private sampleRowSize() {
    if (!this.isVirtual || this.measuredRowSize !== undefined) {
      return
    }
    // Measure the inner option, not the row: the row is already given the
    // estimated height, so sampling it back would just echo the estimate. The
    // option is sized by its own content, so it yields the true uniform height.
    const row = this.listboxEl?.querySelector<HTMLElement>('.n-virtual-row .n-option')
    const size = row?.getBoundingClientRect().height
    if (size && size > 0) {
      this.measuredRowSize = size
      this.virtualizer.getVirtualizer().measure()
    }
  }

  /**
   * Renders only the virtual items currently in view, absolutely positioned
   * inside a full-height sizer so the scrollbar reflects the whole list. Rows take
   * a fixed height from the virtualizer (uniform rows), so no per-row measurement
   * is needed. `setSize`/`posinset` give assistive tech the true total even though
   * most rows aren't in the DOM.
   */
  private renderVirtualOptions(options: ComboboxOption[], items: VirtualItem[]) {
    const total = options.length
    const totalSize = this.virtualizer.getVirtualizer().getTotalSize()

    // Force each row to the virtualizer's size only once the uniform height has
    // been sampled. Before then the row keeps its natural height so the sample
    // reads the real content height rather than echoing the estimate back.
    const sized = this.measuredRowSize !== undefined

    return html`
      <div class="n-virtual-sizer" style="block-size:${totalSize}px">
        ${repeat(
          items,
          item => item.key,
          item => html`
            <div
              class="n-virtual-row"
              data-index=${item.index}
              style=${sized
                ? `block-size:${item.size}px; transform: translateY(${item.start}px)`
                : `transform: translateY(${item.start}px)`}
            >
              ${this.renderListOption(options[item.index], item.index, total)}
            </div>
          `,
        )}
      </div>
    `
  }

  protected renderListOption(option: ComboboxOption, index: number, setSize?: number) {
    const selected = this.isSelected(option)

    return html`
      <div
        id=${this.optionId(index)}
        class=${classMap({
          'n-option': true,
          'is-active': index === this.activeDescendant.activeIndex,
          'is-disabled': Boolean(option.disabled),
        })}
        role="option"
        aria-selected=${this.optionAriaSelected(selected)}
        aria-disabled=${option.disabled ? 'true' : nothing}
        aria-setsize=${ifDefined(setSize)}
        aria-posinset=${ifDefined(setSize === undefined ? undefined : index + 1)}
        @click=${() => this.selectOption(option)}
        @mousemove=${() => this.handleOptionHover(index)}
      >
        ${this.rendersCheck
          ? html`
              <span class="n-option-check">
                ${this.showCheck(selected) ? html`<nord-icon name="interface-checked" size="s"></nord-icon>` : nothing}
              </span>
            `
          : nothing}
        <span class="n-option-label">${this.optionContent(option)}</span>
      </div>
    `
  }

  /** The option's `aria-selected` value. Autocomplete omits it (returns nothing). */
  protected optionAriaSelected(selected: boolean): string | typeof nothing {
    return selected ? 'true' : 'false'
  }

  /** Whether the selected-check icon shows. Autocomplete never shows it. */
  protected showCheck(selected: boolean): boolean {
    return selected
  }

  /**
   * Whether the leading check gutter is rendered. Combobox reserves it so the
   * selected tick sits on the left and every label lines up (matching the
   * filter dropdown); autocomplete has no selection, so it omits the gutter to
   * avoid indenting every suggestion.
   */
  protected get rendersCheck(): boolean {
    return true
  }

  /** The create row. Rendered only when `canCreate`; base returns nothing. */
  protected renderCreate(): unknown {
    return nothing
  }

  protected renderEmpty() {
    // Not a selectable option, and rendered outside the listbox role so an empty
    // listbox doesn't violate aria-required-children. `role="status"` lets
    // assistive tech announce that the search returned nothing.
    if (this.loading) {
      return html`<div class="n-empty" role="status"><nord-spinner size="s"></nord-spinner></div>`
    }

    return html`
      <div class="n-empty" role="status">
        ${this.noResultsText || this.localize.term('noResults')}
      </div>
    `
  }

  protected optionId(index: number) {
    return `${this.comboboxId}-option-${index}`
  }

  /** The id used for the create row (so subclasses can reference it). */
  protected get createId(): string {
    return `${this.comboboxId}-create`
  }

  /** Pointer hover highlights an option (without marking it as keyboard-navigated). */
  protected handleOptionHover(index: number) {
    this.onOptionHover()
    this.activeDescendant.setActiveIndex(index)
  }

  protected handleControlClick() {
    if (this.disabled) {
      return
    }

    // Clicking the control opens the list when `openOnInputClick` is set.
    // Autocomplete (`openOnInputClick = false`) therefore opens only on typing —
    // matching Base UI's `openOnInputClick={false}` — while the combobox
    // (default `true`) opens its list on click.
    if (!this.open && this.openOnInputClick) {
      this.show()
    }

    this.focus()
  }

  protected handleClearClick(e: Event) {
    e.stopPropagation()
    if (this.disabled || this.readonly) {
      return
    }
    this.clear()
  }

  /** Clear the current value. Overridden by clearable variants. */
  clear() {}

  protected handleToggleClick(e: Event) {
    e.stopPropagation()

    if (this.disabled || this.readonly) {
      return
    }

    if (this.open) {
      this.hide()
    }
    else {
      this.show()
    }

    this.focus()
  }
}
